"use client";

/**
 * Pepa 3.2 (conversacional): chat de texto libre con Gemini. Pepa conoce el
 * catálogo real, pregunta por alergias y arma la receta; si la IA no está
 * disponible, se ofrece el flujo clásico de opciones (/pepa/clasico).
 */

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import { IconoAtras, IconoChispa } from "@/components/app/iconos";
import { guardarPlanIA, type LineaPlanIA } from "../pepa-datos";

interface Mensaje {
  rol: "usuario" | "pepa";
  texto: string;
}

const SALUDO: Mensaje = {
  rol: "pepa",
  texto:
    "¡Hola! Soy Pepa 🌱 Cuéntame cómo te has sentido estos días y qué te gustaría mejorar. Y muy importante: ¿tienes alguna alergia o ingrediente que debamos evitar?",
};

const SUGERENCIAS = [
  "Ando sin energía",
  "Digestión pesada",
  "Me enfermo seguido",
  "Quiero bajar de peso",
];

export default function PaginaPepaChat() {
  const router = useRouter();
  const [mensajes, setMensajes] = useState<Mensaje[]>([SALUDO]);
  const [texto, setTexto] = useState("");
  const [pensando, setPensando] = useState(false);
  const [caida, setCaida] = useState(false);
  const listaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listaRef.current?.scrollTo({
      top: listaRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [mensajes.length, pensando]);

  async function enviar(contenido?: string) {
    const msg = (contenido ?? texto).trim();
    if (!msg || pensando) return;
    setTexto("");
    const historial: Mensaje[] = [...mensajes, { rol: "usuario", texto: msg }];
    setMensajes(historial);
    setPensando(true);
    try {
      const res = await fetch("/api/pepa/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mensajes: historial }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setCaida(true);
        setMensajes((m) => [
          ...m,
          {
            rol: "pepa",
            texto:
              "Uy, me quedé sin señal un momento. Puedes intentar de nuevo o usar mis preguntas rápidas de siempre.",
          },
        ]);
        return;
      }
      setMensajes((m) => [...m, { rol: "pepa", texto: data.texto }]);
      if (data.plan) {
        guardarPlanIA(data.plan as LineaPlanIA[], data.texto);
        setTimeout(() => router.push("/pepa/plan"), 900);
      }
    } catch {
      setCaida(true);
      setMensajes((m) => [
        ...m,
        {
          rol: "pepa",
          texto: "Sin conexión. Intenta de nuevo en un momento.",
        },
      ]);
    } finally {
      setPensando(false);
    }
  }

  const esPrimera = mensajes.length === 1;

  return (
    <div className="anim-bz-in relative min-h-(--alto-app) bg-blanco-app">
      <div className="fixed top-0 left-1/2 z-20 flex h-28 w-full max-w-[430px] -translate-x-1/2 items-end gap-3 border-b border-black/5 bg-white px-5 pb-4">
        <button
          type="button"
          aria-label="Volver"
          onClick={() => router.back()}
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#f9f9f9]"
        >
          <IconoAtras color="#221F1F" />
        </button>
        <span className="flex h-[38px] w-[38px] items-center justify-center rounded-[14px] bg-lima">
          <IconoChispa size={20} color="#082411" strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="font-app text-[17px] font-semibold text-tinta">Pepa</div>
          <div className="text-xs text-verde">
            {pensando ? "escribiendo…" : "en línea"}
          </div>
        </div>
      </div>

      <div
        ref={listaRef}
        className="sin-scrollbar fixed top-28 bottom-[150px] left-1/2 w-full max-w-[430px] -translate-x-1/2 overflow-auto px-5 py-[22px]"
      >
        <div className="flex flex-col gap-2.5">
          {mensajes.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.rol === "usuario" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`anim-bz-up max-w-[290px] px-4 py-3.5 text-sm leading-5 whitespace-pre-wrap ${
                  m.rol === "usuario"
                    ? "rounded-[20px] rounded-br-[6px] bg-bosque text-white"
                    : "rounded-[20px] rounded-bl-[6px] bg-humo text-tinta"
                }`}
              >
                {m.texto}
              </div>
            </div>
          ))}
          {pensando && (
            <div className="flex justify-start">
              <div className="anim-bz-up rounded-[20px] rounded-bl-[6px] bg-humo px-4 py-3.5 text-sm text-tinta/50">
                Pepa está escribiendo…
              </div>
            </div>
          )}
          {caida && (
            <button
              type="button"
              onClick={() => router.push("/pepa/clasico")}
              className="mx-auto mt-2 flex h-10 items-center rounded-[20px] border border-verde/40 bg-lima/15 px-4 font-app text-[13px] font-medium text-bosque"
            >
              Usar las preguntas rápidas
            </button>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 z-20 w-full max-w-[430px] -translate-x-1/2 border-t border-black/5 bg-white p-4">
        {esPrimera && (
          <div className="sin-scrollbar mb-3 flex gap-2 overflow-auto">
            {SUGERENCIAS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void enviar(s)}
                className="flex h-9 flex-none items-center rounded-[18px] border border-verde/35 bg-lima/15 px-3.5 font-app text-[13px] font-medium text-bosque"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2.5">
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void enviar()}
            placeholder="Cuéntale a Pepa cómo te sientes…"
            className="h-[52px] min-w-0 flex-1 rounded-[26px] border border-black/10 bg-white px-[18px] text-base outline-0 placeholder:text-tinta/40"
          />
          <button
            type="button"
            aria-label="Enviar"
            onClick={() => void enviar()}
            disabled={pensando || !texto.trim()}
            className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-full bg-verde text-white transition active:scale-[.95] disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <p className="mt-2 text-center text-[10px] leading-3 text-tinta/40">
          Pepa no reemplaza a un médico. Si tienes una condición diagnosticada,
          consulta a tu especialista.
        </p>
      </div>
    </div>
  );
}
